import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { sharedGeolocation } from '../services/GeolocationWatchService';

// Create the geolocation context
const GeolocationServiceContext = createContext(null);

// localStorage key for the location tracking setting
const LOCATION_TRACKING_KEY = 'location_tracking';

// Consumers match gyms within 300m and search for nearby ones within 1km, so a
// coarse network fix is plenty. Asking for high accuracy pins the GPS chip on for
// the whole session, and maximumAge: 0 forbids reusing any fix the OS already has.
export const GEOLOCATION_WATCH_OPTIONS = { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 };

/**
 * Continuously watch the user's location using navigator.geolocation.watchPosition
 * and provide it to child components.
 * Only activates when the user has enabled location tracking in their settings.
 */
export function GeolocationServiceProvider({ children }) {
  const [position, setPosition] = useState(null);
  const [enabled, setEnabled] = useState(() => localStorage.getItem(LOCATION_TRACKING_KEY) === 'true');

  // Listen for setting changes (dispatched from UserProfileModal or on login)
  useEffect(() => {
    const handleChange = () => {
      setEnabled(localStorage.getItem(LOCATION_TRACKING_KEY) === 'true');
    };
    window.addEventListener('location-tracking-changed', handleChange);
    return () => window.removeEventListener('location-tracking-changed', handleChange);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setPosition(null);
      return;
    }
    if (!navigator.geolocation) {
      console.warn('Geolocation is not supported by this browser.');
      return;
    }
    const watchId = sharedGeolocation.watchPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        setPosition({ latitude, longitude, accuracy });
      },
      (error) => {
        console.error('Error watching position:', error);
      },
      GEOLOCATION_WATCH_OPTIONS
    );

    return () => {
      sharedGeolocation.clearWatch(watchId);
    };
  }, [enabled]);

  const value = useMemo(() => ({ position, enabled }), [position, enabled]);

  return (
    <GeolocationServiceContext.Provider value={value}>
      {children}
    </GeolocationServiceContext.Provider>
  );
}

/**
 * Update the location tracking setting in localStorage and notify the provider.
 */
export function setLocationTrackingEnabled(value) {
  localStorage.setItem(LOCATION_TRACKING_KEY, value ? 'true' : 'false');
  window.dispatchEvent(new Event('location-tracking-changed'));
}

/**
 * Convenience hook to access the latest geolocation data in child components.
 */
export function useGeolocationService() {
  return useContext(GeolocationServiceContext);
}